/* -------------------------------------------------------------------------- */
/* --------------------------- THEME : FRAMEWORKS --------------------------- */

// FRAMEWORKS : NODES_MODULES
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import SplitText from "gsap/SplitText";
import ScrollSmoother from "gsap/ScrollSmoother";
import DrawSVGPlugin from "gsap/DrawSVGPlugin";
import MorphSVGPlugin from "gsap/MorphSVGPlugin";
gsap.registerPlugin(
	ScrollSmoother,
	ScrollTrigger,
	SplitText,
	DrawSVGPlugin,
	MorphSVGPlugin
);

import Swiper from "swiper";
import {
	Autoplay,
	// Thumbs,
	EffectFade,
	Navigation,
	Pagination,
	Scrollbar,
} from "swiper/modules";

/* -------------------------------------------------------------------------- */
/* ------------------------ THEME : GLOBAL VARIABLES ------------------------ */

// Default : Smooth scrolling
let scrollSmooth = false;

/* -------------------------------------------------------------------------- */
/* ------------------------- THEME : GLOBAL EVENTS -------------------------- */

// DOCUMENT READY :
document.addEventListener("DOMContentLoaded", () => {
	// remove 'no-js' body class
	document.body.classList.remove("no-js");
	gsapPreloader();
	scrollDetection(160);
	menuBurger();
	menuDropdown();
	initBanner();
	bannerToMarquee();
	popinInit();
	toggleInit();
	accordionInit();
	localizationInit();
	swiperInit();
	srcInit();
	customizeInputFile();
});

// DOCUMENT RESIZED (avec ScrollTrigger)
ScrollTrigger.addEventListener("refreshInit", () => {
	gsapScrollSmootherRefresh();
	bannerToMarquee();
});

/* -------------------------------------------------------------------------- */
/* ----------------------- THEME : GLOBAL FUNCTIONS ------------------------- */

// Activation/désactivation du scroll
function scrollEnabled(bool) {
	if (bool === false || bool === true) {
		const enableScroll = bool === true;

		if (scrollSmooth) {
			scrollSmooth.paused(!enableScroll);
		} else {
			document.documentElement.style.overflowY = enableScroll
				? "auto"
				: "hidden";
		}
	}
}

// Scroll vers une ancre
function scrollToAnchor(scrollOffset = 100) {
	// Fonction pour gérer le défilement
	const scrollToElement = (target) => {
		const element = document.querySelector(target);
		if (element) {
			if (scrollSmooth) {
				// Avec ScrollSmoother
				scrollSmooth.scrollTo(target, true, `top ${scrollOffset}px`);
			} else {
				// Par défaut
				window.scrollTo({
					top:
						element.getBoundingClientRect().top + window.scrollY - scrollOffset,
					behavior: "smooth",
				});
			}
		}
	};

	// au chargement
	const hashInUrl = window.location.hash;
	if (hashInUrl) {
		scrollToElement(hashInUrl);
	}

	// avec un [href="#..."]
	const linksWithAnchor = document.querySelectorAll('a[href^="#"]');
	linksWithAnchor.forEach((link) => {
		link.addEventListener("click", (event) => {
			const anchorHref = link.getAttribute("href");
			if (anchorHref !== "#") {
				event.preventDefault();
				scrollToElement(anchorHref);
			}
		});
	});

	// avec un [data-scrollto]
	const dataWithScrollTo = document.querySelectorAll("[data-scrollto]");
	dataWithScrollTo.forEach((data) => {
		data.addEventListener("click", () => {
			let anchorData = data.getAttribute("data-scrollto");
			if (anchorData === "auto") {
				// Calculer la position de défilement pour 'auto'
				const autoOffset = window.innerHeight + window.scrollY - scrollOffset;
				window.scrollTo({
					top: autoOffset,
					behavior: "smooth",
				});
			} else {
				// Utiliser la valeur de data-scrollto comme cible pour scrollToElement
				scrollToElement(anchorData);
			}
		});
	});
}

// Détection du scroll sur le body
const scrollDetection = (scrollOffset = 0) => {
	let position = window.scrollY;

	if (position > scrollOffset) {
		document.body.classList.add("page-scrolled");
	} else {
		document.body.classList.remove("page-scrolled");
	}

	document.addEventListener("scroll", () => {
		// Détecter la direction du scroll
		let scroll = window.scrollY;
		// Scroll vers le bas
		if (scroll > position) {
			if (scroll < scrollOffset) {
				document.body.classList.remove("page-scrolled");
				document.body.classList.remove("page-scrolling");
			} else {
				document.body.classList.add("page-scrolling");
				document.body.classList.add("page-scrolled");
			}
		}
		// Scroll vers le haut
		else {
			document.body.classList.remove("page-scrolling");
			if (scroll < scrollOffset) {
				document.body.classList.remove("page-scrolled");
			}
		}
		// Scroll max ?
		if (scroll + window.innerHeight >= document.documentElement.scrollHeight) {
			document.body.classList.add("page-scroll-completed");
		} else {
			document.body.classList.remove("page-scroll-completed");
		}
		position = scroll;
	});
};

// Gestion du menu burger
const menuBurger = () => {
	const headerBurgerButton = document.querySelector("#page-header-burger");
	const headerBurgerArea = document.querySelector("#page-header");

	if (headerBurgerButton) {
		headerBurgerButton.addEventListener("click", () => {
			if (document.body.classList.contains("page-with-fullscreenmenu")) {
				scrollEnabled(true);
				document.body.classList.remove("page-with-fullscreenmenu");
			} else {
				scrollEnabled(false);
				document.body.classList.add("page-with-fullscreenmenu");
			}
		});

		document.addEventListener("mouseup", (event) => {
			if (
				document.body.classList.contains("page-with-fullscreenmenu") &&
				!headerBurgerButton.contains(event.target) &&
				headerBurgerButton !== event.target &&
				!headerBurgerArea.contains(event.target) &&
				headerBurgerArea !== event.target
			) {
				scrollEnabled(true);
				document.body.classList.remove("page-with-fullscreenmenu");
			}
		});
	}
};

// Gestion des dropdowns
const menuDropdown = () => {
	const menuItemLinks = document.querySelectorAll(
		'.menu .menu__item .menu__link[href="#"]'
	);

	menuItemLinks.forEach((item) => {
		item.closest(".menu__item").classList.add("menu__item--parent");
		item.addEventListener("click", (event) => {
			event.preventDefault();
			item.closest(".menu__item").classList.toggle("menu__item--active");
		});
	});

	document.addEventListener("mouseup", (event) => {
		const activeMenuItems = document.querySelectorAll(
			".menu .menu__item.menu__item--active"
		);

		activeMenuItems.forEach((activeItem) => {
			if (
				!activeItem.isEqualNode(event.target) &&
				!activeItem.contains(event.target)
			) {
				activeItem.classList.remove("menu__item--active");
			}
		});
	});
};

// Activation/désactivation de la bannière
const initBanner = () => {
	// Vérifie si la bannière a déjà été fermée durant cette session
	if (sessionStorage.getItem("bannerClosed") === "true") {
		return;
	}

	const pageBanner = document.querySelector("#page-banner");
	const pageBannerClose = document.querySelector("#page-banner-close");

	if (pageBanner) {
		// actualisation
		document.body.classList.add("page-with-banner");
		// bouton de fermeture
		pageBannerClose.addEventListener("click", () => {
			document.body.classList.remove("page-with-banner");

			// Enregistre en session que la bannière a été fermée
			sessionStorage.setItem("bannerClosed", "true");
		});
	}
};

// Gestion du défilement horizontal de la bannière
const bannerToMarquee = () => {
	const pageBanner = document.querySelector("#page-banner");

	if (pageBanner) {
		const bannerwrapperWidth = pageBanner.offsetWidth;
		const bannerTextWidth =
			pageBanner.querySelector(".banner__text").offsetWidth;
		if (bannerwrapperWidth > bannerTextWidth + 60) {
			pageBanner.classList.remove("page__banner--scrolling");
		} else {
			pageBanner.classList.add("page__banner--scrolling");
		}
	}
};

// Gestions des actions des [data-toggle]
const toggleInit = () => {
	// Récupération de tous les écouteurs
	const toggleListeners = document.querySelectorAll("[data-toggle]");

	// Écouteur d'événement pour chaque écouteur
	toggleListeners.forEach((togglelistener) => {
		const toggleTargetSelector = togglelistener.dataset.toggle;
		const toggleTarget = toggleTargetSelector
			? document.querySelector(toggleTargetSelector)
			: null;

		togglelistener.addEventListener("click", (event) => {
			if (toggleTarget) {
				// event.stopPropagation();
				toggleTarget.classList.toggle("--active");
			}
		});
	});

	// Gestionnaire pour les clics hors des éléments toggle
	document.addEventListener("click", (event) => {
		toggleListeners.forEach((togglelistener) => {
			const toggleTargetSelector = togglelistener.dataset.toggle;
			const toggleTarget = toggleTargetSelector
				? document.querySelector(toggleTargetSelector)
				: null;
			if (
				toggleTarget &&
				toggleTarget.classList.contains("--active") &&
				!toggleTarget.contains(event.target) &&
				!togglelistener.contains(event.target)
			) {
				toggleTarget.classList.remove("--active");
			}
		});
	});
};

// Initialisation des écouteurs pour les éléments avec l'attribut data-localisation
const localizationInit = () => {
	const localizationListeners = document.querySelectorAll(
		"[data-localisation]"
	);

	// Écouteur d'événement pour chaque écouteur
	localizationListeners.forEach((listener) => {
		listener.addEventListener("mouseover", () => {
			const value = listener.dataset.localisation;
			const targets = document.querySelectorAll(
				`[data-localisation="${value}"]`
			);
			targets.forEach((target) => {
				target.classList.add("--active");
			});
		});
		listener.addEventListener("mouseout", () => {
			const value = listener.dataset.localisation;
			const targets = document.querySelectorAll(
				`[data-localisation="${value}"]`
			);
			targets.forEach((target) => {
				target.classList.remove("--active");
			});
		});
	});
};

// Gestions des actions des [data-accordion]
const accordionInit = () => {
	const accordionListeners = document.querySelectorAll("[data-accordion]");

	accordionListeners.forEach((accordionElement) => {
		accordionElement.addEventListener("click", (event) => {
			event.stopPropagation();

			// Retirer la classe "--active" à tous les accordionListeners et accordionTargets
			accordionListeners.forEach((element) => {
				if (element !== accordionElement) {
					const accordionTarget = document.getElementById(
						element.dataset.accordion
					);
					if (accordionTarget) {
						element.classList.remove("--active");
						accordionTarget.classList.remove("--active");
					}
				}
			});

			// Ajouter la classe "--active" à la cible
			if (accordionElement.dataset.accordion) {
				const currentaccordionTarget = document.getElementById(
					accordionElement.dataset.accordion
				);
				if (currentaccordionTarget) {
					accordionElement.classList.add("--active");
					currentaccordionTarget.classList.add("--active");
				}
			}
		});
	});
};

// Gestions des actions des [data-popin]
const popinInit = () => {
	const popins = document.querySelectorAll("[data-popin]");

	popins.forEach((popin) => {
		popin.addEventListener("click", (event) => {
			// event.preventDefault();

			// ouvrir/fermer la popin
			const thispopinName = popin.dataset.popin;
			if (thispopinName) {
				const thispopinEl = document.querySelector(
					`.page__popin#${thispopinName}`
				);
				thispopinEl.classList.toggle("page__popin--open");
			}

			// mettre les vidéos des popins en pause
			const htmlVideoFrompopin =
				document.querySelectorAll(".page__popin video");
			if (htmlVideoFrompopin.length > 0) {
				htmlVideoFrompopin.forEach((video) => {
					video.pause();
				});
			}
		});
	});

	document.addEventListener("mouseup", (event) => {
		// si une popin est ouverte
		if (
			document.querySelectorAll(".page__popin.page__popin--open").length > 0
		) {
			// clic en dehors de la zone
			if (
				!document
					.querySelector(".page__popin .popin__wrapper")
					.contains(event.target) &&
				document.querySelector(".page__popin .popin__wrapper") !== event.target
			) {
				// fermer la popin
				document.querySelectorAll(".page__popin").forEach((popin) => {
					popin.classList.remove("page__popin--open");
				});

				// mettre les vidéos des popins en pause
				const htmlVideoFrompopin =
					document.querySelectorAll(".page__popin video");
				if (htmlVideoFrompopin.length > 0) {
					htmlVideoFrompopin.forEach((popinVideo) => {
						popinVideo.pause();
					});
				}
			}
		}
	});
};

// Convertir les [data-src] en [src]
const srcInit = () => {
	// Select all elements with a data-src attribute
	const srcElements = document.querySelectorAll("[data-src]");
	srcElements.forEach((element) => {
		element.setAttribute("src", element.getAttribute("data-src"));
		element.removeAttribute("data-src");
	});
};

// Personnaliser les input files (.form__control-file)
const customizeInputFile = () => {
	const fileInputs = document.querySelectorAll(
		'.wpcf7-form-control-wrap input[type="file"]'
	);

	fileInputs.forEach((fileInput) => {
		// Ajouter la classe form__control-file au parent .wpcf7-form-control-wrap
		const parentWrap = fileInput.closest(".wpcf7-form-control-wrap");
		if (parentWrap) {
			parentWrap.classList.add("form__control-file");
		}

		// Créer le label personnalisé
		const customLabel = document.createElement("label");
		const customLabelText = fileInput.closest("[data-label]");
		customLabel.className = "form__control-labelfile";
		if (customLabelText) {
			customLabel.textContent = customLabelText.dataset.label;
		} else {
			customLabel.textContent = "Ajouter un fichier";// Texte par défaut
		}

		// Créer l'élément pour afficher le nom du fichier
		const customFileName = document.createElement("span");
		const customFileNameText = fileInput.closest("[data-placeholder]");
		customFileName.className = "form__control-uploadfile";
		if (customFileNameText) {
			customFileName.textContent = customFileNameText.dataset.placeholder;
		} else {
			customFileName.textContent = "Aucun fichier sélectionné";// Texte par défaut
		}
		// Insérer le label personnalisé et le nom du fichier après l'input original
		fileInput.parentNode.insertBefore(customLabel, fileInput);
		fileInput.parentNode.insertBefore(customFileName, fileInput.nextSibling);

		// Attacher l'input file au label pour qu'il déclenche l'ouverture du sélecteur de fichiers
		customLabel.appendChild(fileInput);

		// Mettre à jour le nom du fichier lors de la sélection
		fileInput.addEventListener("change", function () {
			if (fileInput.files.length > 0) {
				customFileName.textContent = fileInput.files[0].name;
			} else {
				customFileName.textContent = "Aucun fichier sélectionné";
			}
		});
	});
};

/* -------------------------------- (SWIPER) -------------------------------- */

// Swiper : Initialisation des sliders
function swiperInit() {
	// (#related-news)
	if (
		document.querySelectorAll("#related-news .swiper .swiper-slide").length > 1
	) {
		new Swiper("#related-news .swiper", {
			// Core parameters
			slidesPerView: "auto",
			speed: 600,
			resizeObserver: true,
			// Modules
			modules: [Scrollbar],
			scrollbar: {
				el: "#related-news .swiper-scrollbar",
				draggable: true,
			},
		});
	}

	// (#related-nurseries)
	if (
		document.querySelectorAll("#related-nurseries .swiper .swiper-slide")
			.length > 1
	) {
		new Swiper("#related-nurseries .swiper", {
			// Core parameters
			slidesPerView: "auto",
			speed: 600,
			resizeObserver: true,
			// Modules
			modules: [Scrollbar],
			scrollbar: {
				el: "#related-nurseries .swiper-scrollbar",
				draggable: true,
			},
		});
	}

	// (#dates)
	if (document.querySelectorAll("#dates .swiper .swiper-slide").length > 1) {
		new Swiper("#dates .swiper", {
			// Core parameters
			slidesPerView: "auto",
			speed: 600,
			resizeObserver: true,
			loop: true,
			// Modules
			modules: [Autoplay, EffectFade, Navigation, Pagination],
			autoplay: {
				delay: 6000,
			},
			effect: "fade",
			fadeEffect: {
				crossFade: true,
			},
			navigation: {
				nextEl: "#dates .swiper-nav-next",
				prevEl: "#dates .swiper-nav-prev",
			},
			pagination: {
				el: "#dates .swiper-pagination",
				clickable: true,
			},
		});
	}

	// (#feed)
	if (document.querySelectorAll("#feed .swiper .swiper-slide").length > 1) {
		new Swiper("#feed .swiper", {
			// Core parameters
			slidesPerView: "auto",
			speed: 5000,
			resizeObserver: true,
			loop: true,
			freeMode: true,
			// Modules
			modules: [Autoplay],
			autoplay: {
				delay: 1,
				reverseDirection: true,
			},
		});
	}
}

/* --------------------------------- (GSAP) --------------------------------- */

// GSAP : Initialisation du scrollSmoother (avec ScrollSmoother)
function initScrollSmooth() {
	scrollSmooth = ScrollSmoother.create({
		wrapper: "#page-wrapper",
		content: "#page-container",
		smooth: 1,
		smoothTouch: false,
		effects: true,
		normalizeScroll: false,
		ignoreMobileResize: true,
	});
}

// GSAP : Découpage des textes (avec SplitText)
function gsapSplitText() {
	const textsToAnimated = document.querySelectorAll([
		// global
		'[data-animation^="reveal-text"]',
		".elementor-heading-title",
	]);

	textsToAnimated.forEach((_thisText) => {
		new SplitText(_thisText, {
			tag: "span",
			// type: "lines, words, chars",
			type: "lines, words",
			linesClass: "line",
			wordsClass: "word",
			charsClass: "char",
		});
	});

	let heroHometitle = document.querySelector(
		"#hero-home .section__footer__title"
	);
	if (heroHometitle) {
		heroHometitle = new SplitText(heroHometitle, {
			tag: "span",
			type: "lines, words",
			linesClass: "line",
			wordsClass: "word",
		});
	}

	let heroHomeSubtitle = document.querySelector(
		"#hero-home .section__footer__subtitle"
	);
	if (heroHomeSubtitle) {
		heroHomeSubtitle = new SplitText(heroHomeSubtitle, {
			tag: "span",
			type: "lines, words",
			linesClass: "line",
			wordsClass: "word",
		});
	}
}

// GSAP : Animations au scroll (avec ScrollTrigger)
function gsapAnimations() {
	// [data-animation] + .elementor-element
	const elementsToAnimated = document.querySelectorAll([
		"[data-animation]",
		".elementor-heading-title",
		".elementor-element > .elementor-element > .elementor-element:not([data-animation])",
	]);
	elementsToAnimated.forEach((_this) => {
		const delay = _this.getAttribute("data-delay")
			? parseInt(_this.getAttribute("data-delay"), 10)
			: 0;
		ScrollTrigger.create({
			trigger: _this,
			start: "top 100%",
			end: "bottom 0",
			onEnter: () => {
				setTimeout(() => {
					_this.classList.add("--animated");
				}, delay);
			},
			onLeaveBack: () => {
				_this.classList.remove("--animated");
			},
			// markers: true,
		});
	});

	// data-animation="counter"
	const elementsToCounter = document.querySelectorAll(
		'[data-animation="counter"]'
	);
	elementsToCounter.forEach((_this) => {
		const delay = _this.getAttribute("data-delay")
			? parseInt(_this.getAttribute("data-delay"), 10)
			: 0;
		ScrollTrigger.create({
			trigger: _this,
			start: "top 100%",
			end: "bottom 0%",
			// markers: true,
			onEnter: () => {
				setTimeout(() => {
					gsap.from(_this, {
						duration: 1.5,
						textContent: 0,
						snap: { textContent: 1 },
						stagger: {
							each: 1.0,
							onUpdate: function () {
								this.targets()[0].innerHTML = Math.ceil(
									this.targets()[0].textContent
								)
									// conserver les "."
									.toString()
									.replace(/\B(?=(\d{3})+(?!\d))/g, ".")
									// conserver les espaces
									.toString()
									.replace(/\B(?=(\d{3})+(?!\d))/g, "&nbsp;");
							},
						},
					});
				}, delay);
			},
		});
	});

	// data-scrub="spin"
	const elementsToSpin = document.querySelectorAll([
		'body:not(.elementor-editor-active) [data-scrub="spin"]',
	]);
	elementsToSpin.forEach((_this) => {
		const delay = _this.getAttribute("data-delay")
			? parseInt(_this.getAttribute("data-delay"), 10) / 100
			: 1;
		gsap.to(_this, {
			rotate: 360,
			duration: 0.6,
			stagger: 0.1,
			scrollTrigger: {
				trigger: _this,
				start: "top bottom",
				scrub: delay,
				// markers: true,
			},
		});
	});

	// data-animation="draw"
	const elementsToDraw = document.querySelectorAll([
		'body:not(.elementor-editor-active) [data-animation="draw"]',
	]);
	elementsToDraw.forEach((_this) => {
		const delay = _this.getAttribute("data-delay")
			? parseInt(_this.getAttribute("data-delay"), 10) / 100
			: 0;
		gsap.from(_this.querySelectorAll("g > *"), {
			drawSVG: 0,
			ease: "power2.out",
			duration: 1.2,
			delay: delay,
			scrollTrigger: {
				trigger: _this,
				start: "top 75%",
				// markers: true,
			},
		});
	});

	// data-scrub="draw"
	const elementsToDrawing = document.querySelectorAll([
		'body:not(.elementor-editor-active) [data-scrub="draw"]',
	]);
	elementsToDrawing.forEach((_this) => {
		const delay = _this.getAttribute("data-delay")
			? parseInt(_this.getAttribute("data-delay"), 10) / 100
			: 1;
		gsap.from(_this.querySelectorAll("g > *"), {
			drawSVG: 0,
			stagger: 0.1,
			scrollTrigger: {
				trigger: _this,
				start: "top 75%",
				end: "bottom 50%",
				scrub: delay,
				// markers: true,
			},
		});
	});

	// data-scrub="text-reading"
	const textsToReading = document.querySelectorAll([
		'[data-scrub="text-reading"]',
	]);
	textsToReading.forEach((_this) => {
		const delay = _this.getAttribute("data-delay")
			? parseInt(_this.getAttribute("data-delay"), 10) / 100
			: 1;
		gsap.fromTo(
			_this.querySelectorAll(".word"),
			{
				opacity: 0.33,
			},
			{
				opacity: 1,
				ease: "power1.out",
				stagger: 0.3,
				scrollTrigger: {
					trigger: _this,
					start: "top 88%",
					end: "top 22%",
					scrub: delay,
					// markers: true,
				},
			}
		);
	});

	// #hero-home
	const heroHome = document.querySelector("#hero-home");
	if (heroHome) {
		// Timeline d'animation pour heroHome
		let heroTimeline = gsap.timeline({
			scrollTrigger: {
				trigger: heroHome,
				start: "top top",
				end: "bottom center",
				scrub: 2,
				// markers: true,
			},
		});

		// Animation sur le media
		heroTimeline.from(heroHome, {
			"--media-offset": "35%",
			"--media-rounded": "18em",
			// ease: "back.out(1.2)",
			ease: "power2.out",
		});

		// Animation sur le title
		heroTimeline.from(heroHome.querySelectorAll(".section__footer .word"), {
			y: "100%",
			rotate: 5,
			autoAlpha: 0,
			ease: "power1.out",
			duration: 0.6,
			stagger: 0.1,
		});
	}

	// #hero
	const heroPage = document.querySelector("#hero");
	if (heroPage) {
		// Timeline d'animation pour heroPage
		let heroTimeline = gsap.timeline({
			scrollTrigger: {
				trigger: heroPage,
				start: "top top",
				end: "bottom center",
				scrub: 2,
				// markers: true,
			},
		});

		// Animation sur le media
		heroTimeline.from(heroPage, {
			"--media-offset": "10%",
			"--media-rounded": "18em",
			// ease: "back.out(1.2)",
			ease: "power2.out",
		});
	}
}

// Actions exécutées après le preLoader d'accueil
function afterPreloader() {
	document.body.classList.remove("page-loading");
	scrollEnabled(true);
	swiperInit();
	gsapAnimations();
	scrollToAnchor();
}

// GSAP : Rafraîchissement du ScrollSmoother (avec ScrollSmoother)
const gsapScrollSmootherRefresh = () => {
	if (scrollSmooth) {
		// Désactiver sur Élémentor
		if (document.querySelector("body.elementor-editor-active")) {
			scrollSmooth.kill();
		} else {
			scrollSmooth.refresh();
		}
	}
};

// GSAP : Gestion du preloader d'accueil
const gsapPreloader = () => {
	// Chargement des fonctions GSAP préalables
	initScrollSmooth();
	scrollSmooth ? scrollSmooth.scrollTop(0) : window.scrollTo({ top: 0 });
	scrollEnabled(false);
	gsapSplitText();

	// Lancement de l'animation de chargement
	const loaderTimeline = gsap.timeline();
	// Animations spécifiques sur la page d'accueil
	if (
		document.body.classList.contains("home") &&
		!sessionStorage.getItem("played")
	) {
		sessionStorage.setItem("played", true);
		loaderTimeline
			.to("#page-preloader-logo", {
				opacity: 1,
				ease: "power1.out",
				delay: 0.3,
				duration: 0.6,
			})
			// (étape 1 : apparition)
			.to('#page-preloader-logo [data-name="eyes"]', {
				opacity: 0,
				ease: "power4.out",
				duration: 0.3,
			})
			.to(
				'#page-preloader-logo [data-name="blink"]',
				{
					opacity: 1,
					ease: "power4.out",
					duration: 0.3,
				},
				"<"
			)
			.from(
				'#page-preloader-logo [data-name="favicon"]',
				{
					x: "150%",
					ease: "power1.out",
					duration: 0.6,
				},
				"<"
			)
			.fromTo(
				'#page-preloader-logo [data-name="lenfant-roi"]',
				{ opacity: 0, x: "-100" },
				{
					opacity: 1,
					x: 0,
					ease: "power2.out",
					duration: 0.6,
				},
				"<0.5"
			)
			.fromTo(
				'#page-preloader-logo [data-name="dots-1"]',
				{ opacity: 0, x: "100" },
				{
					opacity: 1,
					x: 0,
					ease: "power3.out",
					duration: 0.6,
				},
				"<0.2"
			)
			.to(
				'#page-preloader-logo :is([data-name="favicon"], [data-name="lenfant-roi"])',
				{
					opacity: 0,
					ease: "power1.out",
					duration: 0.6,
				}
			)
			// (étape 2 : transformation)
			.to('#page-preloader-logo [data-name="dots-1"] path', {
				morphSVG: '#page-preloader-logo [data-name="dots-2"] path',
				ease: "back.out(1)",
				// ease: "power2.out",
				duration: 1.2,
			})
			.to(
				"#page-preloader-logo",
				{
					scale: 1,
					ease: "power1.out",
					duration: 0.6,
				},
				"<"
			)
			.to(
				'#page-preloader-logo [data-name="creche-montessori"]',
				{
					opacity: 1,
					ease: "power1.out",
					duration: 0.6,
				},
				"<"
			)
			.to(
				"#page-preloader .preloader__wrapper",
				{
					height: "auto",
					ease: "power4.out",
					duration: 0.6,
				},
				"<"
			)
			// (étape 3 : disparition)
			.set("#page-preloader", {
				visibility: "hidden",
			});
	} else {
		loaderTimeline
			.to("#page-preloader .preloader__wrapper", {
				opacity: 0,
				ease: "power1.out",
				duration: 0.6,
				delay: 0.3,
			})
			.set("#page-preloader", {
				visibility: "hidden",
			});
	}

	// Functions succédentes
	loaderTimeline.add(afterPreloader, loaderTimeline.duration());
};
